import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { takeUIClickEvent } from "../../../../../../../common/ga/ga";
import { TakeUIClickEventParameters } from "../../../../../../../common/ga/types/ga-general-type";
import { ApplicationEnum } from "../../../../../../../constants/constants";
import { fetchMockExamCoursesAsync } from "../../../../../redux/onboarding-slice";
import { CORE_URL } from "../../../../../utils";
import { color } from "../../../../../../../mui-theme/palette";
import { selectMockExamCourses } from "../../../../../redux/selector";
import { debounce, get } from "lodash";
import { selectCandidateProfile } from "../../../../../../auth/redux/selector";

interface HookProps {}
export const useMockExamEntranceCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mockExamCourses = useSelector(selectMockExamCourses);
  const recruitmentConfig = useSelector((state: any) => state.onboarding.configs.Recruitment);
  const candidateProfile = useSelector(selectCandidateProfile);
  const [isExpanded, setIsExpanded] = useState(false);

  const getCardColorStyle = useCallback((status: string) => {
    /* PHKL Custom -s*/
    // if (status === "completed") {
    //   return { statusBackgroundColor: color.greenLight, statusTextColor: color.green };
    // }
    // return { statusBackgroundColor: color.cyanLight, statusTextColor: color.cyan };
    switch (status) {
      case "completed":
        return { statusBackgroundColor: color.greenLight, statusTextColor: color.green };
      case "in_progress":
        return { statusBackgroundColor: color.cyanLight, statusTextColor: color.cyan };
      case "enrolled":
        return { statusBackgroundColor: color.blueLight, statusTextColor: color.blue };
      default:
        return { statusBackgroundColor: color.greyBg, statusTextColor: color.grey33 };
    }
    /* PHKL Custom -e*/
  }, []);

  const mockExams = useMemo(() => {
    const enrolledCourses = mockExamCourses || [];
    return enrolledCourses.map((course: any) => {
      return {
        ...course,
        status: course.enrollment_status,
        ...getCardColorStyle(course.enrollment_status),
      };
    });
  }, [mockExamCourses, getCardColorStyle]);

  const displayedMockExams = useMemo(() => {
    if (isExpanded || mockExams.length <= 3) {
      return mockExams;
    }
    return mockExams.slice(0, 3);
  }, [mockExams, isExpanded]);

  const showViewMore = useMemo(() => {
    return mockExams.length > 3;
  }, [mockExams]);

  useEffect(() => {
    const courseIds =
      recruitmentConfig?.mockExam?.courses?.map((course: any) => course.courseId) || [];

    if (courseIds.length > 0 && candidateProfile?.candidateCode) {
      const debouncedFetch = debounce(
        () => {
          dispatch<any>(fetchMockExamCoursesAsync({ courseIds }));
        },
        300,
        { leading: false, trailing: true },
      );

      debouncedFetch();
      return () => {
        debouncedFetch.cancel();
      };
    }
  }, [dispatch, recruitmentConfig?.mockExam?.courses, candidateProfile]);

  const mockExamEnabled = useMemo(() => {
    const mockExamEnabled = recruitmentConfig?.mockExam?.enabled;
    return mockExamEnabled === true;
  }, [recruitmentConfig]);

  const translations = useMemo(() => {
    return {
      title: t("Recruitment.mock_exam_progress"), // PHKL Custom
      more: t("Common.more"),
      enroll: t("Common.enroll_a_mock_exam_now"),
      status_not_enrolled: t("Common.not_enrolled"), // PHKL Custom
      viewMore: t("Common.show_more"),
      viewLess: t("Common.show_less"),
    };
  }, [t]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const mappingStatusText = useCallback(
    (status: string) => {
      switch (status) {
        case "enrolled":
          return t("Common.enrolled");
        case "completed":
          return t("Common.COMPLETED");
        default:
          return t("Common.in_progress");
      }
    },
    [t],
  );
  const mappingStatusDesc = useCallback(
    (status: string) => {
      switch (status) {
        case "completed":
          return t("Common.your_mock_exam_is_completed");
        default:
          return t("Common.complete_the_mock_exam_now");
      }
    },
    [t],
  );

  const handlePress = useCallback((mockExam: any) => {
    const application = ApplicationEnum.DOCEBO;
    const redirectUrl = `learn/course/${mockExam.course_id}`;
    window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
      window,
      "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
      "",
    )}&application=${application}&grantType=saml&idp=saam`;

    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "MockExam",
      journey: "view_mock_exam_menu",
      stage: "journey_entry",
      screen_id: "SCR_RCRTMT_HM",
      screen_name: "Recruitment-Home-Screen",
      object_name: "MockExam",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  }, []);

  return {
    handlePress,
    translations,
    mockExams,
    displayedMockExams,
    mockExamEnabled,
    mappingStatusText,
    mappingStatusDesc,
    isExpanded,
    showViewMore,
    handleToggleExpand,
  };
};
